import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Zap, Clock, Info } from "lucide-react";
import { toast } from "sonner";
import { Device, deviceService } from "@/services/deviceService";

interface ClearCacheDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  device: Device | null;
  onSuccess?: () => void;
}

export default function ClearCacheDialog({
  open,
  onOpenChange,
  device,
  onSuccess,
}: ClearCacheDialogProps) {
  const [scheduleType, setScheduleType] = useState<"instant" | "scheduled">("instant");
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!device) return;

    if (scheduleType === "scheduled" && (!scheduledDate || !scheduledTime)) {
      toast.error("Lütfen tarih ve saat seçin");
      return;
    }

    try {
      setLoading(true);

      if (scheduleType === "instant") {
        await deviceService.clearCache(device._id);
        toast.success(`${device.name} cihazının cache'i temizleniyor`);
      } else {
        const scheduledDateTime = new Date(`${scheduledDate}T${scheduledTime}`);
        await deviceService.clearCache(device._id, scheduledDateTime);
        toast.success(
          `Cache temizleme ${scheduledDateTime.toLocaleString("tr-TR")} tarihine zamanlandı`
        );
      }

      onOpenChange(false);
      resetForm();
      onSuccess?.();
    } catch (error) {
      toast.error("Cache temizleme sırasında hata oluştu");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setScheduleType("instant");
    setScheduledDate("");
    setScheduledTime("");
  };

  const isFormValid = () => {
    if (scheduleType === "instant") return true;
    return scheduledDate && scheduledTime;
  };

  // Minimum date: bugün
  const today = new Date().toISOString().split("T")[0];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Cache Temizleme</DialogTitle>
        </DialogHeader>

        {/* Bilgilendirme Banner */}
        <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-blue-800">
            Müzik dosyaları hariç tüm cache temizlenecektir. İşlem tamamlandıktan sonra
            cihaz otomatik olarak yeniden başlatılacaktır.
          </p>
        </div>

        {/* Seçenekler */}
        <div className="space-y-3">
          {/* Anlık Temizle */}
          <div
            className={`flex items-start gap-4 border-2 rounded-lg p-4 cursor-pointer transition-all ${
              scheduleType === "instant"
                ? "border-blue-500 bg-blue-50 shadow-sm"
                : "border-gray-200 hover:border-gray-300 hover:shadow-sm"
            }`}
            onClick={() => setScheduleType("instant")}
          >
            <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1 pt-1">
              <h3 className="text-base font-semibold mb-1">Anlık Temizle</h3>
              <p className="text-sm text-muted-foreground">
                Cache hemen temizlenecek
              </p>
            </div>
            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-2 ${
              scheduleType === "instant"
                ? "border-blue-500 bg-blue-500"
                : "border-gray-300"
            }`}>
              {scheduleType === "instant" && (
                <div className="w-2 h-2 rounded-full bg-white"></div>
              )}
            </div>
          </div>

          {/* Zamanla */}
          <div
            className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
              scheduleType === "scheduled"
                ? "border-blue-500 bg-blue-50 shadow-sm"
                : "border-gray-200 hover:border-gray-300 hover:shadow-sm"
            }`}
          >
            <div
              className="flex items-start gap-4"
              onClick={() => setScheduleType("scheduled")}
            >
              <div className="w-12 h-12 rounded-full bg-purple-500 flex items-center justify-center flex-shrink-0">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 pt-1">
                <h3 className="text-base font-semibold mb-1">Zamanla</h3>
                <p className="text-sm text-muted-foreground">
                  Cache belirli bir tarih/saatte temizlenecek
                </p>
              </div>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-2 ${
                scheduleType === "scheduled"
                  ? "border-blue-500 bg-blue-500"
                  : "border-gray-300"
              }`}>
                {scheduleType === "scheduled" && (
                  <div className="w-2 h-2 rounded-full bg-white"></div>
                )}
              </div>
            </div>

            {/* DateTime Picker - Sadece scheduled seçiliyse göster */}
            {scheduleType === "scheduled" && (
              <div className="mt-4 space-y-3 pl-16">
                <div className="space-y-2">
                  <Label htmlFor="date" className="text-sm font-medium">Tarih</Label>
                  <Input
                    id="date"
                    type="date"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    min={today}
                    className="w-full"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="time" className="text-sm font-medium">Saat</Label>
                  <Input
                    id="time"
                    type="time"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                    className="w-full"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false);
              resetForm();
            }}
            disabled={loading}
          >
            İptal
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isFormValid() || loading}
          >
            {loading ? "İşleniyor..." : "Temizle"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
